import { useCallback, useEffect, useRef, useState } from 'react';
import { desktop, type GoyoneDesktop, type PhoneReply, type PhoneState } from './desktop';

type PhoneBridge = NonNullable<GoyoneDesktop['phone']>;

/**
 * الجسرُ من المتصفّح: تطبيقُ سطح المكتب يعمل في الخلفية ويفتح منفذًا على
 * الجهاز نفسه، فتكلّمه الصفحةُ من أيّ متصفّح ويتّصل الهاتفُ كما من التطبيق.
 *
 * يُسبَر مرّةً عند أوّل حاجة (بمهلةٍ قصيرة) ويُتشارك بين المُركِّبات؛
 * وغيابُه — لا تطبيقَ مسطَّبًا أو لا يعمل — يعني الرجوعَ إلى `tel:`.
 */
const LOCAL_BRIDGE = 'http://127.0.0.1:47831/phone';

const callLocal = async (cmd: string, args?: Record<string, unknown>, timeoutMs = 20_000) => {
  const r = await fetch(LOCAL_BRIDGE, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-goyone': '1' },
    body: JSON.stringify({ cmd, args: args ?? {} }),
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!r.ok) throw new Error(`bridge ${r.status}`);
  return (await r.json()) as PhoneReply;
};

let localProbe: Promise<PhoneBridge | null> | null = null;
function localBridge(): Promise<PhoneBridge | null> {
  if (!localProbe) {
    localProbe = callLocal('status', {}, 2500)
      .then((): PhoneBridge => ({ request: (cmd, args) => callLocal(cmd, args) }))
      .catch(() => null);
  }
  return localProbe;
}

/**
 * الهاتفُ المقترن — من الشاشة مباشرة.
 *
 * في المتصفّح يفتح `tel:` تطبيقَ الاتصال (فون لينك) فيعرض الرقمَ ويطلب
 * ضغطةً ثانية، ولا يعرف النظامُ متى أُقفلت المكالمة. في تطبيق سطح المكتب
 * على ويندوز يُطلب الخطُّ مباشرة: ضغطةُ «اتصال» تتّصل، والإنهاءُ والكتمُ
 * والانتظارُ من الشاشة، وإقفالُ المكالمة من الهاتف يصل إلى الشاشة فتفتح
 * نافذةَ النتيجة وحدها.
 *
 * `watch` يُشغّل السؤالَ الدوريّ عن الحالة (كلّ ثانية) — أثناء مكالمةٍ
 * جارية في النظام لا طوال الوقت.
 */
/* ويندوز يُبقي المكالمةَ المنتهية في القائمة لحظاتٍ بهذه الحالات — ليست حيّة. */
const ENDED = new Set(['Disconnected', 'Ended']);

/**
 * رُفعت السمّاعة — وويندوز يسمّيها «Talking».
 *
 * كان الشرطُ «Active» ظنًّا، وهي حالةٌ لا يبلّغها ويندوزُ أصلًا: قُرئت
 * الحالةُ من خطٍّ قائمٍ فإذا هي `Talking`. فكان شرطُ الردِّ لا يتحقّق
 * أبدًا. و«Active» تبقى معها احتياطًا لاختلاف الإصدارات.
 */
const TALKING = new Set(['Talking', 'Active']);

/**
 * لا خطَّ هاتفيًّا الآن.
 *
 * ويندوز لا يكشف خطًّا إلّا حين يكون الهاتفُ متّصلًا فعلًا (لا مقترنًا
 * فحسب) وفون لينك مقترنًا به — فهو وحدَه من يسمح له ويندوز بامتلاك قناة
 * المكالمات؛ أيُّ برنامجٍ آخر يُرفض. فالرسالةُ تقول ما يُفعل، لا «فشل».
 */
export const NO_LINE_MESSAGE =
  'الهاتف غير موصول — افتح بلوتوث الهاتف واضغط اسم الجهاز ليتّصل، وتأكّد من اقترانه في فون لينك، ثم أعد المحاولة';

export function usePairedPhone(watch: boolean) {
  /* داخل التطبيق: جسرُ النافذة. في المتصفّح: المنفذُ المحلّيّ إن وُجد. */
  const [bridge, setBridge] = useState<PhoneBridge | null>(desktop?.phone ?? null);
  useEffect(() => {
    if (desktop) return;
    let stopped = false;
    void localBridge().then((b) => {
      if (!stopped && b) setBridge(() => b);
    });
    return () => {
      stopped = true;
    };
  }, []);
  const [state, setState] = useState<PhoneState | null>(null);
  /* آخر مكالمةٍ رأيناها — لنعرف الإقفالَ حين تختفي. */
  const seenCall = useRef<string | null>(null);
  const [endedFromPhone, setEndedFromPhone] = useState(0);

  const apply = useCallback((reply: PhoneReply) => {
    if (!reply.state) return reply;
    setState(reply.state);
    const call = reply.state.call;
    /*
     * «جاريةٌ» من شاهدين لا من واحد.
     *
     * الخطُّ وحدَه كان الشاهد، وهو يخذل: يُبلّغ «Ended» أو لا يُبلّغ شيئًا
     * والمكالمةُ قائمةٌ على الشاشة — فلا تُعرف بدايةٌ ولا نهاية، ويبقى
     * الشريطُ يسجّل مكالمةً أُقفلت من فون لينك من دقائق.
     *
     * فنافذةُ فون لينك شاهدٌ ثانٍ: تُولد مع المكالمةِ وتموت معها من أيِّ
     * بابٍ أُقفلت. وغيابُ الشاهدَين معًا هو الإقفال.
     */
    const byLine = call && !ENDED.has(call.status);
    const byWindow = reply.state.window === true;
    const live = byLine || byWindow ? call?.id || 'call' : null;
    if (seenCall.current && !live) setEndedFromPhone((n) => n + 1);
    seenCall.current = live;
    return reply;
  }, []);

  const request = useCallback(
    async (cmd: Parameters<PhoneBridge['request']>[0], args?: Record<string, unknown>) => {
      if (!bridge) return { ok: false, error: 'unsupported' } as PhoneReply;
      try {
        return apply(await bridge.request(cmd, args));
      } catch {
        /* المنفذُ المحلّيّ سقط (أُغلق التطبيق) — لا انفجار، بل إخفاقٌ يُقال. */
        return { ok: false, error: 'bridge-down' } as PhoneReply;
      }
    },
    [bridge, apply],
  );

  /* سؤالٌ واحد عند الفتح: هل من هاتفٍ مقترن؟ — لتقوله الشاشةُ قبل أوّل اتصال. */
  useEffect(() => {
    if (!bridge) return;
    let stopped = false;
    void bridge
      .request('status')
      .then((r) => {
        if (!stopped) apply(r);
      })
      .catch(() => undefined);
    return () => {
      stopped = true;
    };
  }, [bridge, apply]);

  useEffect(() => {
    if (!bridge || !watch) return;
    let stopped = false;
    const tick = () => {
      void bridge
        .request('status')
        .then((r) => {
          if (!stopped) apply(r);
        })
        .catch(() => undefined);
    };
    tick();
    const t = window.setInterval(tick, 1000);
    return () => {
      stopped = true;
      window.clearInterval(t);
    };
  }, [bridge, watch, apply]);

  /* بلا مراقبة: يُنسى ما رأيناه، فلا يُحسب إقفالٌ قديم على مكالمةٍ جديدة. */
  useEffect(() => {
    if (!watch) seenCall.current = null;
  }, [watch]);

  const call = state?.call && !ENDED.has(state.call.status) ? state.call : null;

  return {
    /** الجسرُ موجود (تطبيقُ سطح المكتب على ويندوز). */
    available: !!bridge,
    /** مكالمةٌ تجري الآن — من الخطِّ أو من نافذةِ فون لينك. */
    live: !!(call || state?.window),
    /**
     * رُفعت السمّاعة.
     *
     * يُعرف يقينًا من الخطِّ حين يكشفه ويندوز: «Active» لا «Dialing».
     * وحين لا يكشفه — وهو يقع — فلا سبيلَ إلى معرفةِ لحظةِ الردّ، فيُعدّ
     * قيامُ المكالمةِ ردًّا. وتسجيلُ رنّاتٍ زائدةٍ أهونُ من مكالمةٍ تمرُّ
     * بلا تسجيلٍ في شركةٍ تُلزم به.
     */
    answered: call ? TALKING.has(call.status) : !!(state?.active || state?.window),
    /** أيكشفُ ويندوزُ المكالمةَ لنا؟ — وبه يُعرف أنّ «الردّ» ظنٌّ لا يقين. */
    lineVisible: !!call,
    /** مكتومةٌ — من الخطِّ أو من ميكروفونِ الجهازِ حين يرفضُ الخطّ. */
    muted: !!(call?.muted || state?.micMuted),
    /** هاتفٌ مقترنٌ يمكن الاتصالُ منه الآن. */
    ready: !!state?.line?.canDial,
    line: state?.line ?? null,
    call,
    /** يزيد كلّما أُقفلت مكالمةٌ من الهاتف — لتفتح الشاشةُ نافذةَ النتيجة. */
    endedFromPhone,
    dial: (number: string, name?: string) => request('dial', { number, name }),
    end: () => request('end'),
    mute: () => request('mute'),
    unmute: () => request('unmute'),
    hold: () => request('hold'),
    resume: () => request('resume'),
    refresh: () => request('status'),
    /** قبل الاتصال: هل من خطٍّ الآن؟ — يُسأل الجسرُ لا الحالةُ المحفوظة. */
    ensureLine: async () => {
      const r = await request('status');
      return !!r.state?.line?.canDial;
    },
  };
}

export type PairedPhone = ReturnType<typeof usePairedPhone>;

/** ترجمةُ حالة المكالمة كما يبلّغها ويندوز. */
export function phoneStatusLabel(status: string): string {
  switch (status) {
    case 'Dialing':
      return 'يتّصل…';
    /* «Talking» هي ما يبلّغه ويندوز فعلًا، و«Active» احتياطُ إصدارات. */
    case 'Talking':
    case 'Active':
      return 'جارية';
    case 'Held':
      return 'على الانتظار';
    case 'Incoming':
      return 'واردة';
    case 'Disconnected':
    case 'Ended':
      return 'انتهت';
    default:
      return 'متّصل';
  }
}
